import { some } from '@utils/some';
import Cookie from 'js-cookie';
import { BasketDetails } from 'shared-types/Basket';
import { parseTablesDetailsForBasket } from './parseTablesDetailsForBasket';
import { parseSpaExperiencesForBasket } from './parseSpaExperiencesForBasket';
import { addToBasket, AddToBasketProps } from './addToBasket';
import { mergeGuestData } from './mergeGuestData';

export const appendSpaAndTableToRoomsItemInBasket = (props: AddToBasketProps) => {
  const {
    updateBasket,
    createBasket,
    bookingDetails,
    fromDate,
    toDate,
    property,
    language,
    basketDetails,
    showConfirmation,
    spaPackageId,
    spaOpenTime,
    spaCloseTime,
    spaTimeBands,
    maxGuestsPerExperience,
    loggedInUserGuestData,
    packageType,
    experienceGuestStartIndex = 0,
    roomNumber = 0,
    existingBasketId = undefined,
    existingBasketItems = [],
    packageParentId,
    isRepeatedFlexibleEnabled,
    stayDuration,
    experienceDayOffsetsByGroupId,
  } = props;

  const basketId = existingBasketId || Cookie.get('basketId');
  const tablesDetails = bookingDetails?.tables;
  const spaExtras = bookingDetails.spaExtras;
  const basketItems = some(existingBasketItems) ? existingBasketItems : basketDetails?.items || [];

  const guestsByRoom: Record<number, typeof bookingDetails.guests> = {};
  bookingDetails.guests.forEach((guest) => {
    if (!guestsByRoom[guest.roomNumber]) {
      guestsByRoom[guest.roomNumber] = [];
    }
    guestsByRoom[guest.roomNumber].push(guest);
  });

  const totalRooms = Object.keys(guestsByRoom).length;
  const roomGuestsForCurrentRoom = guestsByRoom[roomNumber] || [];

  const tablesDetailsForBasket = tablesDetails
    ? parseTablesDetailsForBasket({ tablesDetails, parentBasketId: packageParentId, fromDate })
    : [];
  const existingGuests = (basketItems || []).flatMap((item) => item.guestDetails);

  const roomGuestDetails = roomGuestsForCurrentRoom.map((g) => g.guestDetails);
  const roomGuestExperiences = roomGuestsForCurrentRoom.map((g) => g.experiences || []);

  const mergedGuests = mergeGuestData(existingGuests, roomGuestDetails);

  const hasValidExperiences = roomGuestExperiences.some((experiences) => experiences.length > 0);

  const spaDetailsForBasket = hasValidExperiences
    ? parseSpaExperiencesForBasket(
        mergedGuests,
        roomGuestExperiences,
        spaExtras,
        spaPackageId,
        fromDate,
        toDate,
        spaOpenTime,
        spaCloseTime,
        maxGuestsPerExperience,
        loggedInUserGuestData ?? null,
        packageType,
        packageParentId,
        isRepeatedFlexibleEnabled,
        stayDuration,
        experienceDayOffsetsByGroupId ?? {},
        bookingDetails.spaNotes,
        spaTimeBands
      )
    : [];

  const items = [...(roomNumber === 0 ? tablesDetailsForBasket : []), ...spaDetailsForBasket];

  const continueWithResponse = (response: { payload?: BasketDetails }) => {
    if (!response?.payload) {
      throw new Error('appendSpaAndTableToRoomsItemInBasket: basket update failed');
    }

    if (roomNumber === totalRooms - 1) {
      showConfirmation();
      return undefined;
    }

    return addToBasket({
      ...props,
      experienceGuestStartIndex: experienceGuestStartIndex + roomGuestDetails.length,
      roomNumber: roomNumber + 1,
      existingBasketId: response.payload.id,
      existingBasketItems: response.payload.items,
    });
  };

  if (basketId) {
    return updateBasket(
      property.id,
      basketId,
      language,
      {
        items: [...basketItems, ...items].filter((item) => !!item),
      },
      true
    ).then(continueWithResponse);
  } else {
    return createBasket(
      property.id,
      language,
      { items: items.filter((item) => !!item) },
      true
    ).then(continueWithResponse);
  }
};
